<template>
    <div class="fill-height bg_auth">
        <div class="d-flex justify-center mb-4">
            <v-img
                src="/img/bobbee_logo_3.png"
                :height="!$vuetify.display.mobile ? '10vh' : ''"
                :width="!$vuetify.display.mobile ? '' : '20vw'"
            />
        </div>
        <div class="pt-4 pb-4 d-flex justify-center">
            <div class="form pt-4 pb-4">
                <RegisterButton
                    v-model:step="step"
                    :offer="data.offer"
                    :loading="loading"
                    :ended="ended"
                    @validNextStep="triggerValidNextStep"
                >
                </RegisterButton>
                <InfoPage
                    v-model:data="data"
                    v-model:step="step"
                    @userFormValidation="userFormValidation"
                    @companyFormValidation="companyFormValidation"
                >
                </InfoPage>
            </div>
        </div>
        <v-row>
            <v-col cols="9">
                <small class="d-flex justify-center align-center mr-24"
                    >This site is protected by reCAPTCHA and the Google
                    <a href="https://policies.google.com/privacy"> Privacy Policy </a> and
                    <a href="https://policies.google.com/terms"> Terms of Service </a> apply.
                </small>
            </v-col>
            <v-col cols="1">
                <div class="d-flex justify-end mr-8 align-center">
                    <v-btn @click="login()" variant="text"> Se connecter ? </v-btn>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
import InfoPage from "./InfoPage.vue";
import RegisterButton from "./RegisterButton.vue";

import {
    registrationInfo,
    userInfo,
    companyInfo,
    Register_Step,
} from "@/types/registerServices.types";
import { Offer_type } from "@/types/offer.types";
import CHECK_USER_EMAIL_INFO from "@/graphql/register/CheckUserInfo.gql";
import CHECK_COMPANY_SIRET_INFO from "@/graphql/register/CheckCompanyInfo.gql";
import CREATE_NEW_ACCOUNT from "@/graphql/register/CreateNewAccount.gql";
import { Component, Vue, toNative } from "vue-facing-decorator";

const grecaptcha = (window as any).grecaptcha;

type Valid_Form = {
    user: boolean;
    company: boolean;
};

@Component({
    components: {
        InfoPage,
        RegisterButton,
    },
})
export class Register extends Vue {
    step: number = 0;

    captchaToken: string = "";
    isCreated: boolean = false;
    showCaptcha: boolean = false;

    validForm: Valid_Form = {
        user: false,
        company: false,
    };

    data: registrationInfo = {
        user: {
            name: "",
            lastname: "",
            email: "",
            phoneNumber: "",
            error: {
                email: "",
            },
        },
        company: {
            siret: "",
            siren: "",
            vatNumber: "",
            name: "",
            legalUnitName: "",
            tradeName: "",
            address: "",
            city: "",
            postalCode: "",
            country: "",
            legalForm: "",
            activities: [],
            salesAccounts: [],
            error: {
                siret: "",
                siren: "",
                vatNumber: "",
            },
        },
        accounting: {
            incomeCategory: "BIC",
            taxRegime: "REAL_NORMAL",
            accountingType: "RECEIVABLES_DEBT",
            payedVAT: true,
        },
        declaration: {
            vatSystem: "Normal",
            vatDeclarationDay: 15,
            vatOption: null,
            taxSystemType: "IS",
            sie: "",
            startBusinessDate: "",
            firstAccountingPeriodEnd: "",
        },
        offer: Offer_type.ACCOUNTING_BASIC,
    };

    loading: boolean = false;
    ended: boolean = false;

    userFormValidation(value) {
        this.validForm.user = value;
    }

    companyFormValidation(value) {
        this.validForm.company = value;
    }

    login() {
        this.$router.push({ name: "login" });
    }

    async validUserInfo() {
        const user: userInfo = this.data.user;
        let isError = false;

        if (!user.name) {
            this.data.user.error.name = "Le prénom est manquant";
            isError = true;
        }

        if (!user.lastname) {
            this.data.user.error.lastname = "Le nom est manquant";
            isError = true;
        }

        if (!user.phoneNumber) {
            this.data.user.error.phoneNumber = "Le numéro de téléphone est manquant";
            isError = true;
        }

        if (!user.email) {
            this.data.user.error.email = "L'adresse mail est manquante";
            isError = true;
        }

        if (!this.validForm.user) {
            return false;
        }

        const result = await this.$apollo.query({
            query: CHECK_USER_EMAIL_INFO,
            variables: {
                email: user.email,
            },
        });

        if (result.data.checkNewUserInfo?.emailError?.length) {
            this.data.user.error.email = result.data.checkNewUserInfo.emailError;
            isError = true;
        }
        if (isError) {
            return false;
        }
        this.data.user.error.email = "";
        return true;
    }

    async validCompanyInfo() {
        const company: companyInfo = this.data.company;
        let isError = false;

        if (!company.siret.length) {
            company.error.siret = "Le siret est manquant";
            isError = true;
        }

        if (!company.legalForm || company.legalForm.length === 0) {
            company.error.legalForm = "La forme légale est manquante";
            isError = true;
        }

        if (!this.validForm.company) {
            return false;
        }

        const result = await this.$apollo.query({
            query: CHECK_COMPANY_SIRET_INFO,
            variables: {
                siret: company.siret,
                siren: company.siren,
                vatNumber: company.vatNumber,
            },
        });

        const data = result.data.checkNewCompanyInfo;
        if (data.siretError.length) {
            this.data.company.error.siret = data.siretError;
            isError = true;
        }
        if (data.sirenError.length) {
            this.data.company.error.siren = data.sirenError;
            isError = true;
        }
        if (data.vatNumberError.length) {
            this.data.company.error.vatNumber = data.vatNumberError;
            isError = true;
        }

        if (isError) {
            return false;
        }

        return true;
    }

    async createNewAccount() {
        this.loading = true;
        let offer = "";

        switch (this.data.offer) {
            case Offer_type.BUSINESS_BASIC:
                offer = "BUSINESS_BASIC";
                break;
            case Offer_type.BUSINESS_PREMIUM:
                offer = "BUSINESS_PREMIUM";
                break;
            case Offer_type.BUSINESS_ESSENTIAL:
                offer = "BUSINESS_ESSENTIAL";
                break;
            case Offer_type.BUSINESS_SMART:
                offer = "BUSINESS_SMART";
                break;
        }
        const result = await this.$apollo.mutate({
            mutation: CREATE_NEW_ACCOUNT,
            variables: {
                user: this.data.user,
                company: this.data.company,
                offer: offer,
                accounting: this.data.accounting,
                declaration: this.data.declaration,
                captchaInfo: {
                    projectId: "oss360",
                    token: this.captchaToken,
                    recaptchaKey: "6LdFWwgqAAAAAHDBTHCqE9MZwA0rTzMLRjRxCY37",
                },
            },
        });
        this.loading = false;
        this.ended = true;
        return true;
    }

    async validNextStep() {
        switch (this.step) {
            case Register_Step.USER_STEP:
                if (await this.validUserInfo()) {
                    this.step = Register_Step.COMPANY_STEP;
                }
                break;
            case Register_Step.COMPANY_STEP:
                if (await this.validCompanyInfo()) {
                    this.step = Register_Step.OFFER_STEP;
                }
                break;
            case Register_Step.ACCOUNTING_STEP:
                this.step = Register_Step.RESUME_STEP;
                break;
            case Register_Step.OFFER_STEP:
                if (this.data.offer != Offer_type.ACCOUNTING_BASIC) {
                    this.step = Register_Step.ACCOUNTING_STEP;
                }
                break;
            case Register_Step.RESUME_STEP:
                console.log(this.data.company.activities);
                await this.captcha();
                break;
            case Register_Step.END_STEP:
                this.$router.push({ name: "login" });
                break;
        }
    }

    triggerValidNextStep() {
        this.validNextStep();
    }

    async captcha() {
        await grecaptcha.enterprise?.ready(async () => {
            this.captchaToken = await grecaptcha.enterprise.execute(
                "6LdFWwgqAAAAAHDBTHCqE9MZwA0rTzMLRjRxCY37",
                { action: "LOGIN" },
            );
            if (await this.createNewAccount()) {
                this.step = Register_Step.END_STEP;
            }
            this.captchaToken = "";
        });
    }
}

export default toNative(Register);
</script>

<style>
.grecaptcha-badge {
    visibility: hidden;
}
</style>

<style scoped>
.bg_auth {
    height: "100%";
    overflow: hidden;
    flex-direction: column;
    justify-content: right;
    background-image: url("../../assets/img/bg_auth.jpg");
    background-size: cover;
    background-repeat: no-repeat;
    background-position-x: center;
}

.form {
    background-color: white;
    border-radius: 25px;
    overflow-y: hidden;
    overflow-x: hidden;
    max-height: 80vh;
    width: 66%;
}
</style>
